/**
 * Social API Types
 * Defines all types used by the social features (posts, comments, notifications, etc.)
 */

// Minimal user info for feed/posts - privacy optimized
export interface MinimalUser {
  id: number;
  firstName?: string;
  lastName?: string;
  photoUrl: string | null;
}

// User basic info (appears in social contexts like notifications, posts, comments)
// Email is NOT included for privacy - only visible via GET /users/
export interface UserBasic {
  id: number;
  firstName?: string;
  lastName?: string;
  name: string;
  location: string;
  dateOfBirth: string; // ISO date string - visible in profile views
  bio: string | null;
  photoUrl: string | null; // Cloudinary URL
  createdAt: string; // ISO datetime string
  membershipLevel: number;
  // Legacy field (use photoUrl instead)
  profilePicture?: string;
}


/**
 * Generic paginated response from the API.
 * Used for endpoints that support pagination.
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Paginated response for user lists (friends, followers, following).
 */
export type UserBasicPaginatedResponse = PaginatedResponse<UserBasic>;

// User search result - minimal information for privacy
// Email and dateOfBirth are NOT included in search results
export interface UserSearchResult {
  id: number;
  name: string; // Full display name from API
  firstName?: string;
  lastName?: string;
  location: string;
  bio: string | null;
  photoUrl: string | null;
  createdAt: string;
  membershipLevel: number;
  dateOfBirth: string;
  // Relationship metadata
  isFriend: boolean;
  isFollowing: boolean;
  followsMe: boolean;
  pendingFriendRequest?: boolean;
  sentFriendRequest?: boolean;
  // Compatibility score with current user (range: -1.0 to 1.0, or null if unavailable)
  compatibilityScore?: number | null;
  // Legacy field (use photoUrl instead)
  profilePicture?: string | null;
  // Group invite eligibility (populated when member_ids is passed to search)
  canInvite?: boolean;
  notFriendsWith?: number[];
}

// Backend returns a plain array for user search
export type UserSearchResponse = UserSearchResult[];

// User profile stats
export interface UserProfileStats {
  friendsCount: number;
  followersCount: number;
  followingCount: number;
  postsCount: number;
  checkinsCount: number;
  listsCount: number;
}

// Favorite cuisine from quiz responses
export interface FavoriteCuisine {
  name: string;
  flag: string;
  learnMoreUrl?: string;
}

// Cuisine expertise - restaurants rated per cuisine
export interface CuisineExpertiseItem {
  cuisine: string;
  icon: string;
  count: number;
}

// User profile response from GET /social/users/{user_id}/
export interface UserProfileResponse {
  // Basic user information
  id: number;
  firstName?: string;
  lastName?: string;
  location: string | null;
  dateOfBirth?: string | null;
  bio?: string | null;
  photoUrl?: string | null;
  createdAt?: string;
  membershipLevel?: number;

  // Relationship status with current user (optional until backend updated)
  isFriend?: boolean;
  isFollowing?: boolean;
  followsMe?: boolean;
  pendingFriendRequest?: boolean;
  sentFriendRequest?: boolean;

  // User statistics (optional until backend updated)
  stats?: UserProfileStats;

  // Compatibility data
  compatibilityScore?: number | null; // Range: -1.0 to 1.0
  compatibilityAvailable?: boolean;

  // Favorite cuisines from quiz
  favoriteCuisines?: FavoriteCuisine[];

  // Cuisine expertise - restaurant counts per cuisine
  cuisineExpertise?: CuisineExpertiseItem[];

  // Privacy opt-in: backend conditionally populates email when shareEmailOnProfile is true
  shareEmailOnProfile?: boolean;
  email?: string | null;
  // Legacy fields (for backward compatibility)
  profilePicture?: string | null;
}

// Block/Unblock
export interface BlockedUser {
  id: number;
  blocker: UserBasic;
  blocked: UserBasic;
  reason: string;
  createdAt: string;
}

export interface BlockUserRequest {
  user_id: number;
  reason?: string;
}

export interface UnblockResponse {
  message: string;
}

// Report System
export type ReportReason =
  | 'spam'
  | 'harassment'
  | 'hate_speech'
  | 'violence'
  | 'inappropriate'
  | 'misinformation'
  | 'impersonation'
  | 'other';

export type ReportContentType = 'post' | 'comment' | 'user' | 'menu_item';

// Reported content details returned by the API
export interface ReportedContent {
  type: ReportContentType;
  deleted?: boolean;
  // Post fields
  urlKey?: string;
  preview?: string;
  authorName?: string;
  authorId?: number;
  createdAt?: string;
  // Comment fields
  postUrlKey?: string;
  // User fields
  userId?: number;
  name?: string;
  photoUrl?: string | null;
  // Menu item fields
  restaurantId?: number;
}

export interface Report {
  id: number;
  reporter: UserBasic;
  contentType: ReportContentType;
  contentId: number;
  reason: ReportReason;
  description: string;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
  reportedContent?: ReportedContent | null;
}

export interface CreateReportRequest {
  content_type: ReportContentType;
  content_id: number;
  reason: ReportReason;
  description?: string;
}

// Posts
export interface PostImage {
  id: number;
  image: string;
  order: number;
  createdAt: string;
}

export interface PostRestaurant {
  id: number;
  name: string;
  address: string;
  types: string[];
  rating: number;
  priceLevel: number;
  imageUrls: string[];
  summary: string;
}

export interface PostList {
  urlKey: string;
  name: string;
  id?: number;
}

export interface Post {
  id: number;
  urlKey: string;
  user: MinimalUser;
  content: string;
  restaurants: PostRestaurant[];
  lists: PostList[];
  photoUrls: string[];
  mentions: MinimalUser[];
  comments?: Comment[];
  createdAt: string;
  updatedAt: string;
  likeCount: number;
  commentCount: number;
  shareCount: number;
  isLiked: boolean;
  likedBy?: MinimalUser[]; // First few users who liked (for social proof/avatar stack)
  // Reaction fields (new emoji-based engagement system)
  userReaction?: ReactionType | null;
  reactionCounts?: ReactionCounts;
  reactedBy?: ReactedByUser[];
  totalReactions?: number;
  // Legacy fields for backward compatibility
  restaurant?: number | null;
  restaurantName?: string | null;
  restaurantList?: number | null;
  listName?: string | null;
  images?: PostImage[];
}

export interface PostsResponse {
  count: number;
  next: number | null;
  previous: number | null;
  results: Post[];
}

// Check-ins
export interface CheckIn {
  id: number;
  user: UserBasic;
  restaurant: number;
  restaurantName: string;
  restaurantAddress: string;
  createdAt: string;
}

export interface CheckInsResponse {
  count: number;
  next: number | null;
  previous: number | null;
  results: CheckIn[];
}

// Relationships
export interface Relationship {
  id: number;
  fromUser: UserBasic;
  toUser: UserBasic;
  status: 'pending' | 'accepted' | 'rejected';
  relationshipType: 'friend' | 'follow';
  createdAt: string;
}

export interface RelationshipRequest {
  toUser: number;
  relationshipType: 'friend' | 'follow';
}

// Comments
export interface Comment {
  id: number;
  user: UserBasic;
  content: string;
  parent: number | null;
  mentions: UserBasic[];
  replies: Comment[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCommentRequest {
  content: string;
  parent?: number;
}

// Notifications
export const NOTIFICATION_TYPES = [
  'friend_request',
  'friend_accepted',
  'post_liked',
  'post_commented',
  'post_shared',
  'comment_liked',
  'comment_reply',
  'new_follower',
  'mention',
  'checkin',
  // Collaborative list notification types
  'collab_invite',
  'collab_invite_privacy',
  'collab_invite_accepted',
  'collab_invite_declined',
  'collab_member_joined',
  'collab_voting_started',
  'collab_voting_ended',
  'collab_round_ending',
  // Proximity notification types (D44a Phase 4)
  'proximity_friend',
  'proximity_outlet',
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export interface NotificationAction {
  key: string;
  label: string;
  kind: 'api' | 'route';
  testid?: string;
  // Contract surface only for `kind: 'api'` — D44a does not execute a generic
  // fetch against this. The client dispatches on `key`, not `url`.
  url?: string;
  route?: string;
  id?: number;
}

export interface Notification {
  id: number;
  recipient: number;
  sender: UserBasic | null;
  notificationType: NotificationType;
  notificationTypeDisplay: string;
  message: string;
  contentData: {
    type:
      | 'post'
      | 'comment'
      | 'relationship'
      | 'checkin'
      | 'activity'
      | 'collaborative_list'
      | 'proximity_friend'
      | 'proximity_outlet';
    id?: number;
    relationshipType?: 'friend' | 'follow';
    status?: 'pending' | 'accepted' | 'rejected';
    actions?: {
      items: NotificationAction[];
      acceptUrl?: string;
      rejectUrl?: string;
      canRespond?: boolean;
    };
    // For posts and activities
    urlKey?: string;
    // For comments (to link back to parent post)
    postUrlKey?: string;
    postId?: number;
    restaurantId?: number;
    // For proximity notifications (D44a Phase 4): counterparty display name and
    // a coarse label like "within 1 mile" — never coordinates.
    name?: string;
    distanceBucket?: string;
    [key: string]: unknown;
  } | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Notification[];
}

export interface NotificationPreferences {
  friendRequests: boolean;
  friendAccepted: boolean;
  postLikes: boolean;
  postComments: boolean;
  postShares: boolean;
  commentLikes: boolean;
  commentReplies: boolean;
  newFollowers: boolean;
  mentions: boolean;
  checkins: boolean;
  emailEnabled: boolean;
  emailFrequency: 'instant' | 'daily' | 'weekly' | 'never';
  pushEnabled: boolean;
}

// Post Interactions
export interface PostInteraction {
  id: number;
  user: UserBasic;
  interactionType: 'like' | 'share';
  createdAt: string;
}

// Reactions (Emoji-based engagement replacing likes)
export type ReactionType = 'fire' | 'yummy' | 'perfect' | 'craving' | 'love';

export const REACTION_EMOJIS: Record<ReactionType, string> = {
  fire: '🔥',
  yummy: '😋',
  perfect: '💯',
  craving: '🤤',
  love: '❤️',
};

export const REACTION_LABELS: Record<ReactionType, string> = {
  fire: 'Fire',
  yummy: 'Yummy',
  perfect: 'Perfect',
  craving: 'Craving',
  love: 'Love',
};

export interface Reaction {
  id: number;
  user: MinimalUser;
  reactionType: ReactionType;
  emoji: string;
  createdAt: string;
}

export interface ReactedByUser {
  user: MinimalUser;
  reactionType: ReactionType;
  emoji: string;
}

export interface ReactionCounts {
  fire?: number;
  yummy?: number;
  perfect?: number;
  craving?: number;
  love?: number;
}

export interface ReactionSummary {
  totalCount: number;
  counts: ReactionCounts;
  userReaction: ReactionType | null;
  recentUsers: ReactedByUser[];
}

// Check-in creation
export interface CreateCheckInRequest {
  restaurant: number;
  message?: string;
  latitude: number;
  longitude: number;
}

// Feed Activities
export type ActivityType =
  | 'restaurantRated'
  | 'restaurantRatingUpdated'
  | 'menuItemRated'
  | 'menuItemRatingUpdated'
  | 'quizCompleted'
  | 'quizUpdated'
  | 'checkedIn'
  | 'listMadePublic'
  | 'listViewed'
  | 'listSaved'
  | 'listCopied'
  | 'listUpdated'
  | 'notesAdded'
  | 'notesUpdated'
  | 'ratingAlgorithmChanged';

export interface FeedActivity {
  id: number;
  urlKey: string;
  user: UserBasic;
  activityType: ActivityType;
  /** Not sent for `type: 'activity'` feed items (FeedActivitySerializer omits it); only grouped activities carry it. */
  activityTypeDisplay?: string;
  metadata: Record<string, unknown>;
  isLiked: boolean;
  likeCount?: number;
  shareCount?: number;
  commentCount?: number;
  likedBy?: MinimalUser[]; // First few users who liked (for social proof/avatar stack)
  // Reaction fields (new emoji-based engagement system)
  userReaction?: ReactionType | null;
  reactionCounts?: ReactionCounts;
  reactedBy?: ReactedByUser[];
  totalReactions?: number;
  createdAt: string;
}

// Grouped Activities
export interface GroupedItem {
  timestamp: string;
  rating?: number;
  restaurant?: {
    id: number;
    name: string;
    cuisines: string[];
    imageUrl?: string;
    city?: string;
  };
  restaurantList?: {
    id: number;
    name: string;
    itemCount?: number;
    visibility?: string;
    createdAt?: string;
  };
  menuItem?: {
    id: number;
    name: string;
    restaurant?: {
      id: number;
      name: string;
    };
  };
  quiz?: {
    id: number;
    name?: string;
  };
}

export interface GroupedActivity {
  type: 'grouped_activity';
  activityType: ActivityType;
  activityTypeDisplay: string;
  count: number;
  user: UserBasic;
  timestamp: string;
  timestampEarliest: string;
  timestampLatest: string;
  items: GroupedItem[];
}

// Announcements
export type AnnouncementType = 'system' | 'promotion' | 'feature' | 'maintenance';
export type AudienceType = 'all' | 'pro' | 'location' | 'custom';

export interface Announcement {
  id: number;
  title: string;
  content: string;
  announcementType: AnnouncementType;
  audienceType: AudienceType;
  image: string | null;
  linkUrl: string | null;
  isActive: boolean;
  createdAt: string;
  expiresAt: string | null;
  createdByName: string;
}

// Feed Items
export type FeedItemType = 'post' | 'activity' | 'announcement' | 'grouped_activity';

export interface FeedItem {
  id?: number; // Optional for grouped_activity
  type: FeedItemType;
  timestamp: string;
  data: Post | FeedActivity | Announcement | GroupedActivity;
}

export interface FeedResponse {
  count: number;
  next: number | null;
  previous: number | null;
  results: FeedItem[];
}

// Stories (24-hour ephemeral content)
export type StoryMediaType = 'image' | 'video';

export interface StoryRestaurant {
  id: number;
  name: string;
  imageUrl?: string;
}

export interface Story {
  id: number;
  user: MinimalUser;
  mediaType: StoryMediaType;
  mediaUrl: string;
  thumbnailUrl?: string | null;
  duration?: number | null; // For videos (15-60 seconds)
  restaurant?: StoryRestaurant | null;
  caption: string;
  createdAt: string;
  expiresAt: string;
  viewCount: number;
  isViewed: boolean; // Whether current user has viewed
  /** Text overlays applied to this story (images only) */
  textOverlays?: StoryTextOverlay[];
}

export interface StoryView {
  id: number;
  user: MinimalUser;
  viewedAt: string;
}

export interface UserStoriesGroup {
  user: MinimalUser;
  stories: Story[];
  hasUnviewed: boolean;
  latestStoryAt: string;
}

export interface StoriesResponse {
  results: UserStoriesGroup[];
}

/** Shape of a single text overlay as stored in story metadata */
export interface StoryTextOverlay {
  id: string;
  text: string;
  x: number;
  y: number;
  scale: number;
  color: string;
  bold: boolean;
  italic: boolean;
}

export interface CreateStoryRequest {
  media_url: string;
  media_type: StoryMediaType;
  thumbnail_url?: string;
  duration?: number;
  restaurant?: number;
  caption?: string;
  /** Client-side text overlays stored as metadata on the story (camelCase per frontend convention) */
  textOverlays?: StoryTextOverlay[];
}

// Polls (24-hour voting on restaurants)
export interface PollRestaurant {
  id: number;
  name: string;
  imageUrl?: string;
  cuisines?: string[];
}

export interface PollOption {
  id: number;
  restaurant: PollRestaurant;
  order: number;
  voteCount: number;
  votePercentage: number;
}

export interface Poll {
  id: number;
  question: string;
  options: PollOption[];
  totalVotes: number;
  isActive: boolean;
  userVote: number | null; // Option ID the user voted for
  createdAt: string;
  expiresAt: string;
}

export interface PostWithPoll extends Post {
  poll?: Poll | null;
}

export interface CreatePollRequest {
  question: string;
  options: number[]; // Array of restaurant IDs (2-4)
}

export interface VotePollRequest {
  option_id: number;
}

export interface VotePollResponse {
  message: string;
  optionId: number;
}

// ============================================================================
// PHONE VERIFICATION TYPES
// ============================================================================

/**
 * Request to send OTP to a phone number for verification.
 */
export interface SendOTPRequest {
  phone_number: string;
  country_code?: string; // ISO 3166-1 alpha-2 (e.g., "US")
}

/**
 * Response after successfully sending an OTP.
 */
export interface SendOTPResponse {
  message: string;
  expires_in_seconds: number;
}

/**
 * Request to verify an OTP code.
 */
export interface VerifyOTPRequest {
  phone_number: string;
  code: string;
  country_code?: string;
}

/**
 * Response after successfully verifying phone number.
 */
export interface VerifyOTPResponse {
  message: string;
  phone_number: string; // E.164 formatted
}

// Menu Item Reports
export type MenuItemReportType =
  | 'does_not_exist'
  | 'wrong_spelling'
  | 'discontinued'
  | 'duplicate'
  | 'other';

/**
 * Request payload for reporting a menu item via POST /menu-item-reports/
 * `reportType` and `otherText` map to snake_case on the wire via apiClient transform.
 */
export interface ReportMenuItemRequest {
  restaurantId: number;
  menuItemId: number;
  reportType: MenuItemReportType | string;
  otherText?: string;
}

// Restaurant page reports ("Report This Page", paire-app#106)
/** Slugs mirror `RestaurantReport.REPORT_TYPES` in paire-api (models/restaurant.py). */
export type RestaurantReportType =
  | 'fake_page'
  | 'wrong_location'
  | 'moved'
  | 'permanently_closed'
  | 'bad_information'
  | 'decency_violation';

/**
 * Request payload for POST /restaurant-reports/. The apiClient transforms
 * these camelCase keys to `restaurant_id` / `report_type` on the wire.
 * The endpoint answers 201 for a new report and 200 when this member already
 * has the same report open — both are success from the member's view.
 */
export interface ReportRestaurantRequest {
  restaurantId: number;
  reportType: RestaurantReportType;
}

/**
 * Request to match contacts by email and/or phone numbers.
 */
export interface ContactsMatchAllRequest {
  emails?: string[];
  phone_numbers?: string[];
  country_code?: string;
}

/**
 * Response from contact matching (both email and phone).
 */
export interface ContactsMatchAllResponse {
  matches: UserSearchResult[];
}
